
























































































































































































































































































































































































































































































import { Component, Prop, Vue } from 'vue-property-decorator';
import MaterialCard from '@/components/material/Card/index.vue';
import { AppModule } from '@/store/modules/app';
import { UserModule } from '@/store/modules/user';
import {
  IExternalAccount,
  LanguageEnum,
  CountryEnum,
  IUserTokenData,
  IDelegatedAccount,
} from '@/api/types';
import {
  listGroups,
  listAccounts,
  getAccount,
  putAccount,
  IPutAccountRequest,
  IAddDelegatedAccountsRequest,
  IRemoveDelegatedAccountsRequest,
  addDelegatedAccounts,
  removeDelegatedAccounts,
} from '@/api/users';
import { formatApiError } from '@/utils/index';

type ItemType = {
  text: string;
  value: string;
};

type WithPassword = {
  password: string;
};

type WithIsActive = {
  isActive: boolean;
};

type EditingDelegatedAccount = IAddDelegatedAccountsRequest.DelegatedAccountInfo & {
  accountId: string;
  showPassword: boolean;
  needPassword: boolean;
};

@Component({
  name: 'AccountDetail',
  components: {
    MaterialCard,
  },
})
export default class extends Vue {
  @Prop({ required: true }) private id!: string;
  private account = {} as IExternalAccount;
  private supplierId = '';
  private showPassword = false;
  private loading = true;
  private editing = false;
  private valid = false;
  private editingAccount = {} as IExternalAccount & WithPassword & WithIsActive;
  private editingDelegatedAccoutns: EditingDelegatedAccount[] = [];
  private showEditingPassword = false;
  private languageItems: ItemType[] = Array.from(Object.values(LanguageEnum))
    .filter(l => (l as LanguageEnum) !== LanguageEnum.UNKNOWN_LANGUAGE)
    .map(l => ({
      text: l,
      value: l,
    }));
  private countryItems: ItemType[] = Array.from(Object.values(CountryEnum)).map(
    c => ({
      text: c,
      value: c,
    })
  );
  private groupItems: ItemType[] = [];
  private accounts: Map<string, IExternalAccount> = new Map();
  private accountItems: ItemType[] = [];
  private dialog = false;
  private dialogConfirmHandler = this.resetDialog;
  private dialogTextTitle = '';
  private dialogTextFooter = '';

  get isDialogFullscreen() {
    return false && this.$vuetify.breakpoint.smAndDown;
  }

  get canDisplayResult() {
    return !!this.account && !!this.account.id && !this.loading;
  }

  get canDisplayNoData() {
    return (!this.account || !this.account.id) && !this.loading;
  }

  get noDataText() {
    return this.$t('No {msg}', {
      msg: (this.$t('Account') as string).toLowerCase(),
    });
  }

  get canUpdate() {
    return this.valid;
  }

  created() {
    this.fetchData();
  }

  private async fetchData(noLoading = false) {
    if (!noLoading) {
      this.loading = true;
      AppModule.StartProgress();
    }

    await Promise.all([
      this.fetchThenLoadSupplierId(),
      this.fetchThenLoadAccount(),
      this.fetchThenLoadGroups(),
      this.fetchThenLoadAccounts(),
    ]);

    if (!noLoading) {
      this.loading = false;
      AppModule.StopProgress();
    }
  }

  private fetchThenLoadSupplierId() {
    this.supplierId = (JSON.parse(
      UserModule.token
    ) as IUserTokenData).supplierId;
  }

  private async fetchThenLoadAccount() {
    try {
      const res = await getAccount({ accountId: this.id });
      this.account = res;
    } catch (err) {
      this.$toast.error(
        this.$t('Failed to {do} due to {err}', {
          do: (this.$t('Fetch {msg}', {
            msg: this.$t('Account'),
          }) as string).toLowerCase(),
          err: formatApiError(this.$i18n, err),
        }) as string,
        {
          icon: 'mdi-alert',
        }
      );
    }
  }

  private async fetchThenLoadGroups() {
    try {
      const res = await listGroups({});
      this.groupItems = res.groups.map(g => ({
        text: g.name,
        value: g.id!,
      }));
    } catch (err) {
      this.$toast.error(
        this.$t('Failed to {do} due to {err}', {
          do: (this.$t('Fetch {msg}', {
            msg: this.$t('Group'),
          }) as string).toLowerCase(),
          err: formatApiError(this.$i18n, err),
        }) as string,
        {
          icon: 'mdi-alert',
        }
      );
    }
  }

  private async fetchThenLoadAccounts() {
    try {
      const res = await listAccounts({});
      this.accounts = new Map(res.accounts.map(a => [a.id, a]));
      this.accountItems = res.accounts
        .filter(a => a.id !== this.id)
        .map(a => ({
          text: a.fullName,
          value: a.id,
        }));
    } catch (err) {
      this.$toast.error(
        this.$t('Failed to {do} due to {err}', {
          do: (this.$t('Fetch {msg}', {
            msg: this.$t('Account'),
          }) as string).toLowerCase(),
          err: formatApiError(this.$i18n, err),
        }) as string,
        {
          icon: 'mdi-alert',
        }
      );
    }
  }

  private resetDialog() {
    this.dialog = false;
    this.dialogTextTitle = '';
    this.dialogTextFooter = '';
    this.$nextTick(() => {
      this.dialogConfirmHandler = this.resetDialog;
    });
  }

  private showDialogForUpdate() {
    this.dialogTextTitle = `${this.$t('Update {msg}', {
      msg: this.$t('Account'),
    })}`;
    this.dialogTextFooter = `${this.$t('Is it OK?')}`;
    this.dialogConfirmHandler = () => {
      this.resetDialog();
      this.updateAccount();
    };
    this.dialog = true;
  }

  private showDialogForResetEditing() {
    this.dialogTextTitle = `${this.$t('Reset')}`;
    this.dialogTextFooter = `${this.$t('Is it OK?')}`;
    this.dialogConfirmHandler = () => {
      this.resetDialog();
      this.startEditingAccount();
    };
    this.dialog = true;
  }

  private showDialogForAbort() {
    this.dialogTextTitle = `${this.$t('Cancel {msg}', {
      msg: this.$t('Update {msg}', {
        msg: this.$t('Account'),
      }),
    })}`;
    this.dialogTextFooter = `${this.$t('Is it OK?')}`;
    this.dialogConfirmHandler = () => {
      this.resetDialog();
      this.abortEditingAccount();
    };
    this.dialog = true;
  }

  private startEditingAccount() {
    this.editing = true;
    this.editingAccount = JSON.parse(JSON.stringify(this.account));
    this.editingAccount.password = this.editingAccount.decryptedPassword;
    this.editingAccount.isActive = !this.editingAccount.isInactive;
    this.editingDelegatedAccoutns = this.editingAccount.delegatedAccounts.map(
      a => ({
        accountId: a.accountId,
        showPassword: false,
        needPassword: false,
        supplierId: a.supplierId,
        username: a.username,
        password: '',
      })
    );
  }

  private abortEditingAccount() {
    this.editing = false;
    this.editingAccount = {} as IExternalAccount & WithPassword & WithIsActive;
    this.editingDelegatedAccoutns = [];
  }

  private async updateAccount() {
    this.loading = true;
    AppModule.StartProgress();

    try {
      const updateAccount = JSON.parse(
        JSON.stringify(this.editingAccount)
      ) as IExternalAccount & WithPassword;

      const req: IPutAccountRequest = {
        id: updateAccount.id,
        username: updateAccount.username,
        password: updateAccount.password,
        groupId: updateAccount.groupId,
        locale: updateAccount.locale,
        fullName: updateAccount.fullName,
        isInactive: updateAccount.isInactive,
        version: updateAccount.version,
      };

      await putAccount(req);
      await this.updateDelegatedAccounts();
    } catch (err) {
      this.$toast.error(
        this.$t('Failed to {do} due to {err}', {
          do: (this.$t('Update {msg}', {
            msg: this.$t('Account'),
          }) as string).toLowerCase(),
          err: formatApiError(this.$i18n, err),
        }) as string,
        {
          icon: 'mdi-alert',
        }
      );
    }

    this.abortEditingAccount();
    await this.fetchData(true);
    AppModule.StopProgress();
    this.loading = false;
  }

  private async updateDelegatedAccounts() {
    const addDelegatedAccountsMap: Map<
      string,
      IAddDelegatedAccountsRequest.DelegatedAccountInfo
    > = new Map();
    const getDelegatedAccountsMapKey = (
      account: IDelegatedAccount | EditingDelegatedAccount
    ) => `${account.supplierId}#:#${account.accountId}`;
    const removeDelegatedAccountsMap: Map<
      string,
      IRemoveDelegatedAccountsRequest.DelegatedAccountInfo
    > = new Map();

    this.account.delegatedAccounts.forEach(a =>
      removeDelegatedAccountsMap.set(getDelegatedAccountsMapKey(a), {
        supplierId: a.supplierId,
        accountId: a.accountId,
      })
    );
    this.editingDelegatedAccoutns.forEach(a => {
      if (removeDelegatedAccountsMap.has(getDelegatedAccountsMapKey(a))) {
        removeDelegatedAccountsMap.delete(getDelegatedAccountsMapKey(a));
      } else {
        addDelegatedAccountsMap.set(
          a.accountId
            ? getDelegatedAccountsMapKey(a)
            : `${a.supplierId}#:#${a.username}`,
          {
            supplierId: a.supplierId,
            username: a.username,
            password: a.password,
          }
        );
      }
    });

    try {
      await this.addDelegatedAccounts(
        Array.from(addDelegatedAccountsMap.values())
      );
      await this.removeDelegatedAccounts(
        Array.from(removeDelegatedAccountsMap.values())
      );
    } catch (err) {
      this.$toast.error(
        this.$t('Failed to {do} due to {err}', {
          do: (this.$t('Update {msg}', {
            msg: this.$t('Delegated account'),
          }) as string).toLowerCase(),
          err: formatApiError(this.$i18n, err),
        }) as string,
        {
          icon: 'mdi-alert',
        }
      );
    }
  }

  private async addDelegatedAccounts(
    delegatedAccounts: IAddDelegatedAccountsRequest.DelegatedAccountInfo[]
  ) {
    if (delegatedAccounts.length > 0) {
      await addDelegatedAccounts({
        accountId: this.id,
        delegatedAccounts: delegatedAccounts,
      });
    }
  }

  private async removeDelegatedAccounts(
    delegatedAccounts: IRemoveDelegatedAccountsRequest.DelegatedAccountInfo[]
  ) {
    if (delegatedAccounts.length > 0) {
      await removeDelegatedAccounts({
        accountId: this.id,
        delegatedAccounts: delegatedAccounts,
      });
    }
  }

  private addEditingDelegatedAccount() {
    this.editingDelegatedAccoutns.push({
      accountId: '',
      showPassword: false,
      needPassword: true,
      supplierId: this.supplierId,
      username: '',
      password: '',
    });
  }

  private removeEditingDelegatedAccount(delegatedAccountIndex: number) {
    this.editingDelegatedAccoutns.splice(delegatedAccountIndex, 1);
  }

  private handleEditingDelegatedAccountIdInput(
    accountId: string,
    delegatedAccountIndex: number
  ) {
    this.editingDelegatedAccoutns[
      delegatedAccountIndex
    ].username = this.accounts.get(accountId)!.username;
  }

  private getRequiredRules(
    msg: any,
    exception: (v: any) => boolean = () => false
  ) {
    return [
      (v: string) =>
        exception(v) ||
        !!v ||
        (this.$t('{msg} is required', {
          msg: this.$te(msg) ? this.$t(msg) : msg,
        }) as string),
    ];
  }
}
